import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/axiosInstance";
import { useSubscription } from "../contexts/SubscriptionContext";

const CarrierHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountType, setAccountType] = useState("owner");
  const navigate = useNavigate();

  // Get subscription validation function
  const { isSubscriptionValid } = useSubscription();
  const hasValidSubscription = isSubscriptionValid();

  useEffect(() => {
    // Get account type from localStorage
    const user = JSON.parse(localStorage.getItem("carrierUser"));
    if (user?.accountType) {
      setAccountType(user.accountType);
    }
  }, []);

  const goToDashboard = () => {
    navigate("/");
  };

  const handleLogout = async () => {
    const user = JSON.parse(localStorage.getItem("carrierUser"));

    if (user?._id && user.deviceId) {
      try {
        await api.post("/carriers/logout", {
          carrierId: user._id,
          deviceId: user.deviceId,
        });
      } catch (err) {
        console.error("Logout error:", err);
      }
    }

    localStorage.removeItem("carrierUser");
    api.defaults.headers.common["Authorization"] = "";
    navigate("/");
  };

  return (
    <>
      {/* Sidebar - Desktop */}
      <div className="hidden sm:flex flex-col justify-between w-64 h-screen bg-dark text-light fixed left-0 top-0 z-50 shadow-lg p-6">
        {/* Top section */}
        <div>
          <h1 className="mb-10 cursor-pointer" onClick={goToDashboard}>
            <img
              src="/assets/FleetyBlue.png"
              alt="Fleety Logo"
              className="h-12"
            />
          </h1>

          <nav className="flex flex-col gap-4">
            <Link
              to="/carrier/dashboard"
              className="block text-left font-semibold text-lg hover:text-primary"
            >
              Carrier Dashboard
            </Link>

            {/* Only show Assigned Drivers if subscription is valid */}
            {hasValidSubscription && (
              <Link
                to="/carrier/assigned-drivers"
                className="block text-left font-semibold text-lg hover:text-primary"
              >
                Assigned Drivers
              </Link>
            )}

            {/* Only show Favorites if subscription is valid */}
            {hasValidSubscription && (
              <Link
                to="/carrier/favorites"
                className="block text-left font-semibold text-lg hover:text-primary"
              >
                Favorites
              </Link>
            )}

            {/* Only show Packages if accountType is 'owner' */}
            {accountType === "owner" && (
              <Link
                to="/carrier/package"
                className="block text-left font-semibold text-lg hover:text-primary"
              >
                Packages
              </Link>
            )}

            <Link
              to="/carrier/live-demo-request"
              className="block text-left font-semibold text-lg hover:text-primary"
            >
              Live Demo / Onboarding
            </Link>
            <Link
              to="/carrier/watch-demo"
              className="block text-left font-semibold text-lg hover:text-primary"
            >
              Demo Videos
            </Link>
            <Link
              to="/carrier/report-issues"
              className="block text-left font-semibold text-lg hover:text-primary"
            >
              Report Problem
            </Link>
          </nav>
        </div>

        {/* Bottom logout button */}
        <div>
          <button
            onClick={handleLogout}
            className="bg-primary text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-primary-dark transition"
          >
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </div>

      {/* Top Bar - Mobile */}
      <div className="sm:hidden flex justify-between items-center px-4 py-4 bg-dark text-white shadow-md sticky top-0 z-[100]">
        <h1 className="text-2xl text-primary font-bold">
          <img
            src="/assets/FleetyBlue.png"
            alt="Fleety Logo"
            className="h-10"
          />
        </h1>
        <button
          className="text-2xl focus:outline-none"
          onClick={() => setMenuOpen(true)}
          aria-label="Open Menu"
        >
          <i className="fas fa-bars"></i>
        </button>
      </div>

      {/* Overlay - Mobile */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-[150]"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}

      {/* Sidebar - Mobile Slide-In */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-dark text-light z-[200] transform transition-transform duration-300 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        } p-6`}
      >
        <div className="flex justify-between items-center mb-8">
          <div className="text-2xl font-bold text-primary">
            <img
              src="/assets/FleetyBlue.png"
              alt="Fleety Logo"
              className="h-10"
            />
          </div>
          <button
            className="text-white text-2xl"
            onClick={() => setMenuOpen(false)}
            aria-label="Close Menu"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Stack links vertically */}
        <div className="flex flex-col gap-2 mb-8">
          <Link
            to="/carrier/dashboard"
            onClick={() => setMenuOpen(false)}
            className="text-left font-semibold text-lg hover:text-primary"
          >
            Dashboard
          </Link>

          {/* Only show Assigned Drivers if subscription is valid */}
          {hasValidSubscription && (
            <Link
              to="/carrier/assigned-drivers"
              onClick={() => setMenuOpen(false)}
              className="text-left font-semibold text-lg hover:text-primary"
            >
              Assigned Drivers
            </Link>
          )}

          {/* Only show Favorites if subscription is valid */}
          {hasValidSubscription && (
            <Link
              to="/carrier/favorites"
              onClick={() => setMenuOpen(false)}
              className="text-left font-semibold text-lg hover:text-primary"
            >
              Favorites
            </Link>
          )}

          {/* Only show Packages if accountType is 'owner' */}
          {accountType === "owner" && (
            <Link
              to="/carrier/package"
              onClick={() => setMenuOpen(false)}
              className="text-left font-semibold text-lg hover:text-primary"
            >
              Packages
            </Link>
          )}

          <Link
            to="/carrier/live-demo-request"
            onClick={() => setMenuOpen(false)}
            className="text-left font-semibold text-lg hover:text-primary"
          >
            Live Demo / Onboarding
          </Link>
          <Link
            to="/carrier/watch-demo"
            onClick={() => setMenuOpen(false)}
            className="text-left font-semibold text-lg hover:text-primary"
          >
            Demo Videos
          </Link>
          <Link
            to="/carrier/report-issues"
            onClick={() => setMenuOpen(false)}
            className="text-left font-semibold text-lg hover:text-primary"
          >
            Report Problem
          </Link>
        </div>

        <button
          onClick={() => {
            handleLogout();
            setMenuOpen(false);
          }}
          className="bg-primary text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-primary-dark transition"
        >
          <i className="fas fa-sign-out-alt"></i> Logout
        </button>
      </div>
    </>
  );
};

export default CarrierHeader;
